import { Flex } from "@chakra-ui/react";
import React from "react";
import {
  AttendanceTypes,
  FORM_STATUS,
  FormTabsTypes,
  OFFER_STATUS,
  SearchAndFilterTypes,
  VisibilityOptions,
  WAITLIST_STATUS,
} from "src/constants";
import {
  GetDashboardFormStatusResponse,
  GetDashboardFormSubstatusResponse,
} from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import {
  useOrganization,
  UseOrganizationType,
} from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { DATA_EXPORT_ID } from "./DashboardChart";
import { StatusCard } from "./StatusCard";

export const STATUS_SUMMARY_EXPORT_ID = "export-status-summary";

type Props = {
  formTemplateId: string;
  remoteData: [
    RD.RemoteData<unknown, GetDashboardFormStatusResponse>,
    RD.RemoteData<unknown, GetDashboardFormSubstatusResponse>
  ];
  hasSchoolRankingSection: boolean;
  attendance?: AttendanceTypes;
};
export const StatusSummary: React.FC<Props> = ({
  formTemplateId,
  remoteData: [formStatusRD, formSubstatusRD],
  hasSchoolRankingSection,
  attendance,
}) => {
  const { glossary } = useGlossary();
  const [
    inProgressRD,
    submittedRD,
    verifiedRD,
    lotteryReadyRD,
    waitlistedRD,
    offeredRD,
    acceptedRD,
  ] = React.useMemo(() => {
    return [
      formStatusRD.map((data) =>
        calculateStatusTotal(formTemplateId, "InProgress", data)
      ),
      formStatusRD.map((data) =>
        calculateStatusTotal(formTemplateId, "Submitted", data)
      ),
      formStatusRD.map((data) =>
        calculateStatusTotal(formTemplateId, "Verified", data)
      ),
      formStatusRD.map((data) =>
        calculateStatusTotal(formTemplateId, "LotteryReady", data)
      ),
      formSubstatusRD.map((data) =>
        calculateSubstatusTotal(formTemplateId, "Waitlisted", data)
      ),
      formSubstatusRD.map((data) =>
        calculateSubstatusTotal(formTemplateId, "Offered", data)
      ),
      formSubstatusRD.map((data) =>
        calculateSubstatusTotal(formTemplateId, "Accepted", data)
      ),
    ];
  }, [formStatusRD, formSubstatusRD, formTemplateId]);
  const orgRD = useOrganization();
  return (
    <Flex
      wrap="wrap"
      width="full"
      gap="4"
      {...{ [DATA_EXPORT_ID]: STATUS_SUMMARY_EXPORT_ID }}
    >
      <StatusCard
        total={inProgressRD}
        status={FORM_STATUS["InProgress"]}
        label="Form responses"
        url={generateStatusUrl(orgRD, formTemplateId, "InProgress", attendance)}
      />
      <StatusCard
        total={submittedRD}
        status={FORM_STATUS["Submitted"]}
        label="Form responses"
        url={generateStatusUrl(orgRD, formTemplateId, "Submitted", attendance)}
      />
      <StatusCard
        total={verifiedRD}
        status={FORM_STATUS["Verified"]}
        label="Form responses"
        url={generateStatusUrl(orgRD, formTemplateId, "Verified", attendance)}
      />
      <StatusCard
        total={lotteryReadyRD}
        status={FORM_STATUS["LotteryReady"]}
        label="Form responses"
        url={generateStatusUrl(
          orgRD,
          formTemplateId,
          "LotteryReady",
          attendance
        )}
      />
      {hasSchoolRankingSection ? (
        <>
          <StatusCard
            total={waitlistedRD}
            status={WAITLIST_STATUS["Waitlisted"]}
            label={glossary`School submissions`}
            url={generateSubstatusUrl(
              orgRD,
              formTemplateId,
              "Waitlisted",
              attendance
            )}
          />
          <StatusCard
            total={offeredRD}
            status={OFFER_STATUS["Offered"]}
            label={glossary`School submissions`}
            url={generateSubstatusUrl(
              orgRD,
              formTemplateId,
              "Offered",
              attendance
            )}
          />
          <StatusCard
            total={acceptedRD}
            status={OFFER_STATUS["Accepted"]}
            label={glossary`School submissions`}
            url={generateSubstatusUrl(
              orgRD,
              formTemplateId,
              "Accepted",
              attendance
            )}
          />
        </>
      ) : null}
    </Flex>
  );
};

function generateStatusUrl(
  orgRD: UseOrganizationType,
  formTemplateId: string,
  status: Status,
  attendance?: AttendanceTypes
): string {
  const params = new URLSearchParams();
  params.set(SearchAndFilterTypes.Tab, FormTabsTypes.All);
  params.set(SearchAndFilterTypes.Status, status);
  params.set(SearchAndFilterTypes.Visibility, VisibilityOptions.Visible);
  if (attendance) {
    params.set(SearchAndFilterTypes.Attendance, attendance);
  }
  return orgRD
    .map((organization) =>
      Url.OrgAdmin.Forms.index({
        organization,
        formTemplateId,
        params: params.toString(),
      })
    )
    .withDefault("#");
}

function generateSubstatusUrl(
  orgRD: UseOrganizationType,
  formTemplateId: string,
  substatus: Substatus,
  attendance?: AttendanceTypes
): string {
  const params = new URLSearchParams();
  params.set(SearchAndFilterTypes.Tab, FormTabsTypes.All);
  params.set(SearchAndFilterTypes.SubStatus, substatus);
  params.set(SearchAndFilterTypes.Visibility, VisibilityOptions.Visible);
  if (attendance) {
    params.set(SearchAndFilterTypes.Attendance, attendance);
  }
  return orgRD
    .map((organization) =>
      Url.OrgAdmin.Forms.index({
        organization,
        formTemplateId,
        params: params.toString(),
      })
    )
    .withDefault("#");
}

function calculateStatusTotal(
  formTemplateId: string,
  status: Status,
  formStatus: GetDashboardFormStatusResponse
): number {
  let total = 0;
  for (const row of formStatus.data) {
    if (row.form_template_id !== formTemplateId || row.status !== status) {
      continue;
    }
    let count = 0;
    if (row === undefined) continue;
    if (row.count === null) continue;
    if (typeof row.count === "number") {
      count = row.count;
    } else {
      count = parseInt(row.count);
    }
    total += count;
  }
  return total;
}

function calculateSubstatusTotal(
  formTemplateId: string,
  substatus: Substatus,
  formSubstatus: GetDashboardFormSubstatusResponse
): number {
  let total = 0;
  for (const row of formSubstatus.data) {
    if (
      row.form_template_id !== formTemplateId ||
      row.substatus !== substatus
    ) {
      continue;
    }
    let count = 0;
    if (row === undefined) continue;
    if (row.count === null) continue;
    if (typeof row.count === "number") {
      count = row.count;
    } else {
      count = parseInt(row.count);
    }
    total += count;
  }
  return total;
}

type Status = `${GQL.form_status_enum}`;
type Substatus = `${GQL.waitlist_status_enum}` | `${GQL.offer_status_enum}`;
